// Production page, /production/:id, on src/ui (HD-8; DESIGN rev 2.1 §3.2 Workbench, gate pick Production C). Dark tier
// always, rail sidebar, desktop only; below 1024 px a read-only phone view (production/PhoneView.jsx, HOTFIX-1). Anatomy: the shell's page header (title, client, "Open workbench", the stage
// moves and More) · the stage strip (Script · Shoot · Edit · Review · Post, numbered; ?stage= deep link) · the stage
// body · on the Edit stage the scrub dock, a right panel of 360 px that collapses (?dock=open|closed, kept per user) ·
// the side panels at the far right (Notes, Project file) on a 36 px icon strip.
// The Workbench is this page in its editing mode: Edit stage, dock open, URL ?stage=edit&dock=open. Two clicks from
// Live: a row opens this page, the header's primary action "Open workbench" opens the mode (the rail's Workbench item
// links there too).
//
// Every call, permission and side effect is the old page's (the retired Live video-card modal's, P-8):
//   GET /api/live-production/:id/page, GET /api/team-members; PATCH /api/live-production/:id (Save), …/status,
//   …/schedule-reshoot, …/archive; POST …/activate; DELETE /api/live-production/:id; GET /api/video-assets?liveProductionId,
//   …/archived?liveProductionId, /api/video-assets/:id/download, DELETE /api/video-assets/:id; GET + POST /api/transcriptions
//   (video_asset and raw_footage); POST /api/downloads/zip, /api/downloads/footage-zip and GET /api/downloads/zip/:jobId
//   (every 3 s); GET /api/footage?linked_video_card_id (every 5 s while Process clips runs, every 8 s while a clip
//   transcribes); POST /api/adc/:id/process; GET /api/footage/:id/download; GET /api/client-characters?clientId;
//   GET /api/cut-lists/card/:id (header badge); GET /api/scripts/:id (CSV export); the transcript poll every 8 s.
//   New video versions go through the global Upload sheet (enqueueVideoUpload: multipart, then POST /api/video-assets/confirm).
// External accounts get a plain "not available" state and no call (the API refuses them).
// Retired here (Owner ruling, readiness reports leave the product): the Edit stage's "Scrub is waiting on transcripts" list
// (the same Transcribe call stays on each Shoot row), the readiness fix links, MissedLinesForCard
// (GET /api/missed-lines/card/:id) and the Shoot stage coverage section. See components/SceneScrubPanel.jsx.
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Archive, ArchiveRestore, ArrowLeft, CalendarDays, ChevronDown, Copy, Download, ExternalLink, FileBox, FileDown, MonitorPlay,
  MoreHorizontal, NotebookPen, PanelRightOpen, Pencil, Play, Send, Trash2, TriangleAlert, Undo2, Upload,
} from 'lucide-react';
import api from '../lib/api';
import * as lfe from '../lib/longformEpisodes';
import { useAuth } from '../context/AuthContext';
import { exportSceneCSV } from '../lib/scriptExport';
import { cardTitle } from '../lib/cardTitle';
import { LIVE_TRANSITIONS, BACK_STEP, formatDate, toInputDate } from '../lib/productionStatus';
import { canSetProducer, cardChanges, confirmPayload, filmedHold } from '../lib/filmedHold';
import { minutesText, parseMinutes } from '../lib/runtimeMinutes';
import ProductionNotes from '../components/ProductionNotes';
import VideoPlayer from '../components/VideoPlayer';
import ProjectFileField from '../components/ProjectFileField';
import ShareLinkManager from '../components/ShareLinkManager';
import SceneScrubPanel from '../components/SceneScrubPanel';
import SchedulePlanModal from '../components/SchedulePlanModal';
import ScriptCast from '../components/ScriptCast';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import DatePicker from '../ui/DatePicker';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import Grid from '../ui/Grid';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Menu from '../ui/Menu';
import Popover from '../ui/Popover';
import Select from '../ui/Select';
import Skeleton from '../ui/Skeleton';
import StageChip from '../ui/StageChip';
import Table from '../ui/Table';
import Tag from '../ui/Tag';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { useMedia } from '../shell/AppShell';
import { clean } from '../shell/copy';
import { useUploads, useUploadDone } from '../shell/uploads';
import CookingPhaseBanner from './production/CookingPhaseBanner';
import FootageList from './production/FootageList';
import PhoneView from './production/PhoneView';
import { STAGES, STAGE_LABEL, STATUS_STAGE, STAGE_INDEX, ladderOf, transitionLabel, plural, openUrl } from './production/shared';

const VIDEO_EXT = /\.(mp4|mov|webm|avi|mpeg|mpg)$/i;
const ACCEPTED_EXT = '.mp4,.mov,.webm,.avi,.mpeg,.mpg';
const RAIL_WORD = { done: 'Done', active: 'Current', waiting: 'Waiting' };
// A date in mono, or the words "Not set" in the UI face (formatDate's empty value is a dash)
const sentence = v => (v ? `${String(v).charAt(0).toUpperCase()}${String(v).slice(1)}` : '');
const dayCell = s => (s ? <span className="ui-tc">{formatDate(s)}</span> : <span className="ui-text-3">Not set</span>);

export default function Production() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const external = !!user?.isExternal;
  const [data, setData] = useState(null);      // { record, segments, scheduler }
  const [members, setMembers] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (external) return;                      // the API refuses external accounts: no call, a plain state instead
    try {
      const [p, m] = await Promise.all([api.get(`/api/live-production/${id}/page`), api.get('/api/team-members')]);
      setData(p.data); setMembers(m.data || []); setError('');
    } catch (e) { setError(e.response?.status === 404 ? 'This production was not found. It may have been deleted.' : (e.response?.data?.error || 'This production did not load.')); }
  }, [id, external]);
  useEffect(() => { load(); }, [load]);

  if (external || error || !data) {
    return <ProductionState external={external} error={error} onRetry={load} onBack={() => navigate(external ? '/dashboard' : '/live')} />;
  }
  return <ProductionView key={data.record.id} initial={data} members={members} user={user} onReload={load} />;
}

// Loading, error and the external account's "not available": the shell header plus one plain state.
function ProductionState({ external, error, onRetry, onBack }) {
  usePageHeader({ title: 'Production', actions: !external ? <Button variant="quiet" icon={ArrowLeft} onClick={onBack}>Live production</Button> : undefined });
  return (
    <div className="ui ui-page" data-testid={external ? 'production-not-available' : error ? 'production-error' : 'production-loading'}>
      {external
        ? <EmptyState message="Productions are not available on client accounts. Your team shares cuts with you for review." action={{ label: 'Go to home', onClick: onBack }} />
        : error
          ? <ErrorState title="The production did not load" message={clean(error)} onRetry={onRetry} />
          : <div className="ui-stack" role="status" aria-label="Loading the production"><Skeleton width="40%" /><Skeleton variant="block" height={240} /><Skeleton width="60%" /></div>}
    </div>
  );
}

function ProductionView({ initial, members, user, onReload }) {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const uploads = useUploads();
  const [searchParams, setSearchParams] = useSearchParams();
  const narrow = useMedia('(max-width: 1023px)');
  const userId = user?.id;
  const [record, setRecord] = useState(initial.record);
  // Segments refresh whenever the page reloads its data (the Shoot-stage plan modal saves, then calls onReload): D-088.
  const [segments, setSegments] = useState(initial.segments || []);
  useEffect(() => { setSegments(initial.segments || []); }, [initial.segments]);
  // The record too: a saved plan recomputes the derived shoot date and may move the status.
  useEffect(() => { setRecord(initial.record); }, [initial.record]);
  const [planOpen, setPlanOpen] = useState(false);
  const [scheduler] = useState(initial.scheduler || []);
  const status = record.status;

  // ── stage (strip), dock and side panel ──────────────────────────────────────
  const statusStage = STATUS_STAGE[status] || 'script';
  const urlStage = searchParams.get('stage');
  const [stage, setStageState] = useState(STAGES.includes(urlStage) ? urlStage : statusStage);
  useEffect(() => { if (STAGES.includes(urlStage) && urlStage !== stage) setStageState(urlStage); }, [urlStage]); // eslint-disable-line react-hooks/exhaustive-deps
  function setStage(s) { setStageState(s); const p = new URLSearchParams(searchParams); p.set('stage', s); setSearchParams(p, { replace: true }); }
  // §3.2: panel state persists per user. The URL's dock= wins (the Workbench link carries dock=open).
  const dockKey = `hf-wb-dock:${userId}`, panelKey = `hf-wb-panel:${userId}`;
  const readPref = (k, d) => { try { return localStorage.getItem(k) || d; } catch { return d; } };
  const writePref = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };
  const [dockPref, setDockPref] = useState(() => readPref(dockKey, 'open'));
  const dockParam = searchParams.get('dock');
  const dockOpen = dockParam ? dockParam === 'open' : dockPref !== 'closed';
  function setDock(open) {
    const v = open ? 'open' : 'closed'; setDockPref(v); writePref(dockKey, v);
    const p = new URLSearchParams(searchParams); p.set('dock', v); setSearchParams(p, { replace: true });
  }
  const inWorkbench = urlStage === 'edit' && dockParam === 'open';
  function openWorkbench() {
    setStageState('edit'); setDockPref('open'); writePref(dockKey, 'open');
    const p = new URLSearchParams(searchParams); p.set('stage', 'edit'); p.set('dock', 'open'); setSearchParams(p);
  }
  const [panel, setPanelState] = useState(() => readPref(panelKey, 'none'));   // 'notes' | 'file' | 'none'; HD-9 0a: closed until the viewer opens one
  function togglePanel(p) { const next = panel === p ? 'none' : p; setPanelState(next); writePref(panelKey, next); }

  // ── form (modal parity: dirty form + Save) ──────────────────────────────────
  const blankForm = r => ({
    actorName: r.actorName ?? '', talentId: r.talentId ?? '', shootLeadId: r.shootLeadId ?? '', editorId: r.editorId ?? '',
    producerId: r.producerId ?? '', runtimeMinutes: minutesText(r.runtimeMinutes),
    frameLink: r.frameLink ?? '', frameFolderLink: r.frameFolderLink ?? '', fileName: r.fileName ?? '', fileLocation: r.fileLocation ?? '',
    postedDate: toInputDate(r.postedDate),
    editScheduledDate: toInputDate(r.editScheduledDate), editNotes: r.editNotes ?? '', notes: r.notes ?? '',
  });
  const [form, setForm] = useState(() => blankForm(record));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [reshootDate, setReshootDate] = useState('');
  const [replacing, setReplacing] = useState(false);
  const replacingRef = useRef(false);
  useEffect(() => { replacingRef.current = replacing; }, [replacing]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  function setField(key, value) { setForm(prev => ({ ...prev, [key]: value })); setDirty(true); }

  // ── video asset ─────────────────────────────────────────────────────────────
  const [videoAsset, setVideoAsset] = useState(null);
  const [assetLoading, setAssetLoading] = useState(true);
  const [archivedVersions, setArchivedVersions] = useState([]);
  const [zipJob, setZipJob] = useState(null);
  const zipPollRef = useRef(null);
  const [transcription, setTranscription] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const txPollRef = useRef(null);
  useEffect(() => {
    api.get(`/api/video-assets?liveProductionId=${record.id}`).then(r => setVideoAsset(r.data)).catch(() => {}).finally(() => setAssetLoading(false));
    api.get(`/api/video-assets/archived?liveProductionId=${record.id}`).then(r => setArchivedVersions(r.data)).catch(() => {});
  }, [record.id]);
  useEffect(() => { if (!videoAsset) return; api.get(`/api/transcriptions/video_asset/${videoAsset.id}`).then(r => setTranscription(r.data)).catch(() => {}); }, [videoAsset?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!videoAsset) return;
    if (transcription?.status === 'pending' || transcription?.status === 'processing') {
      txPollRef.current = setInterval(() => { api.get(`/api/transcriptions/video_asset/${videoAsset.id}`).then(r => { if (r.data?.status !== 'pending' && r.data?.status !== 'processing') clearInterval(txPollRef.current); setTranscription(r.data); }).catch(() => {}); }, 8000);
    }
    return () => clearInterval(txPollRef.current);
  }, [transcription?.status, videoAsset?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // R-1/R-2: a new version replaces the player when its upload is confirmed (the server archives the current one), as the
  // old uploader's onUploadComplete did; the upload itself runs in the global Upload sheet.
  useUploadDone(item => {
    if (item.meta?.productionId !== record.id) return;
    setVideoAsset(item.result);
    if (replacingRef.current) { setReplacing(false); setTranscription(null); refreshArchived(); }
  });
  const upload = [...uploads.items].reverse().find(i => i.meta?.productionId === record.id && i.status !== 'done');
  function takeVideo(file) {
    if (!file) return;
    if (!VIDEO_EXT.test(file.name) && !(file.type || '').startsWith('video/')) { toast('Choose a video file: MP4, MOV, WebM or AVI.', { tone: 'warn' }); return; }
    uploads.enqueueVideo({ file, clientId: record.clientId, liveProductionId: record.id });
  }

  // ── raw footage ─────────────────────────────────────────────────────────────
  const [rawFootage, setRawFootage] = useState([]);
  const [playingClipId, setPlayingClipId] = useState(null);
  const [adcStartingIds, setAdcStartingIds] = useState(() => new Set());
  const [footageZip, setFootageZip] = useState(null);
  const footageZipPollRef = useRef(null);
  const [footageSelectMode, setFootageSelectMode] = useState(false);
  const [footageDeselected, setFootageDeselected] = useState(() => new Set());
  const adcPollRef = useRef(null);
  const [faceCast, setFaceCast] = useState([]);                                 // W-10: the card's script cast, the face dropdown's first group
  const loadRawFootage = useCallback(() => { api.get(`/api/footage?linked_video_card_id=${record.id}`).then(r => { setRawFootage(r.data.footage || []); setFaceCast(r.data.faceCast || []); }).catch(() => {}); }, [record.id]);
  useEffect(() => { loadRawFootage(); }, [loadRawFootage]);
  // Faces (D-154): the registry loads once, only when a clip has faces and the viewer may name them.
  const [faceChars, setFaceChars] = useState(null);
  const faceRegistryClientId = rawFootage.find(c => c.visualFaces?.length && c.canNameFaces)?.registryClientId || null;
  useEffect(() => { if (!faceRegistryClientId) return; api.get(`/api/client-characters?clientId=${faceRegistryClientId}`).then(r => setFaceChars((r.data?.characters || []).filter(c => !c.isArchived))).catch(() => setFaceChars([])); }, [faceRegistryClientId]);
  const onClipFaces = useCallback((clipId, faces) => setRawFootage(prev => prev.map(c => c.id === clipId ? { ...c, visualFaces: faces } : c)), []);
  const anyAdcRunning = rawFootage.some(c => !c.parentFootageId && (c.adcStatus === 'pending' || c.adcStatus === 'processing'));
  useEffect(() => { if (!anyAdcRunning) return; adcPollRef.current = setInterval(loadRawFootage, 5000); return () => clearInterval(adcPollRef.current); }, [anyAdcRunning, loadRawFootage]);
  // U-1/U-3: transcribe a clip from the page (the Raw footage page's POST, same optimistic flip) and poll while any clip
  // is in flight, so the scrub dock remounts on the new transcript count.
  const [transcribingIds, setTranscribingIds] = useState(() => new Set());
  const txClipPollRef = useRef(null);
  const anyClipTranscribing = rawFootage.some(c => !c.parentFootageId && (c.transcriptionStatus === 'pending' || c.transcriptionStatus === 'processing'));
  useEffect(() => { if (!anyClipTranscribing) return; txClipPollRef.current = setInterval(loadRawFootage, 8000); return () => clearInterval(txClipPollRef.current); }, [anyClipTranscribing, loadRawFootage]);
  const completedTranscripts = rawFootage.filter(c => !c.parentFootageId && c.transcriptionStatus === 'complete').length;
  async function handleTranscribeClip(clipId) {
    if (transcribingIds.has(clipId)) return;
    setTranscribingIds(prev => new Set(prev).add(clipId));
    setRawFootage(prev => prev.map(c => c.id === clipId ? { ...c, transcriptionStatus: 'pending' } : c));
    try { await api.post('/api/transcriptions', { source_type: 'raw_footage', source_id: clipId }); }
    catch (err) { toast(clean(err.response?.data?.error) || 'The transcription did not start. Try again.', { tone: 'danger' }); setRawFootage(prev => prev.map(c => c.id === clipId ? { ...c, transcriptionStatus: c.transcriptionStatus === 'pending' ? null : c.transcriptionStatus } : c)); }
    finally { setTranscribingIds(prev => { const n = new Set(prev); n.delete(clipId); return n; }); }
  }
  useEffect(() => () => { [zipPollRef, footageZipPollRef, txPollRef, adcPollRef, txClipPollRef].forEach(r => r.current && clearInterval(r.current)); }, []);

  // ── the scrub's cut-list status for the header badge ────────────────────────
  const [cutListInfo, setCutListInfo] = useState(null);
  const loadCutListInfo = useCallback(() => { if (!record.scriptId) return; api.get(`/api/cut-lists/card/${record.id}`).then(r => setCutListInfo(r.data)).catch(() => {}); }, [record.id, record.scriptId]);
  useEffect(() => { loadCutListInfo(); }, [loadCutListInfo]);

  // ── permissions (byte-for-byte the modal's) ─────────────────────────────────
  const isAdmin = user?.roles?.includes('Admin') || user?.roles?.includes('Power User');
  // Longform (D-089): an episode card is an ordinary card with episode fields. The scrub needs spoken text.
  const isEpisode = Boolean(record.episodeNumber) && record.script?.scriptBody?.format === 'Longform';
  const scriptHasDialogue = Array.isArray(record.script?.scriptBody?.scenes) && record.script.scriptBody.scenes.some(r => String(r?.line || r?.narration || '').trim());
  // D-123: an episode's scenes are positional (its block between EPISODE BREAK rows, numbered from 1).
  const epBlock = isEpisode ? lfe.blockForEpisode(record.script?.scriptBody?.scenes, record.episodeNumber) : null;
  const sceneRange = epBlock ? { from: epBlock.sceneFrom, to: epBlock.sceneTo, episode: record.episodeNumber, positional: true }
    : (record.sceneFrom != null || record.sceneTo != null) ? { from: record.sceneFrom, to: record.sceneTo, episode: record.episodeNumber ?? null, positional: false } : null;
  const isShootLead = user?.roles?.includes('Producer');
  const isEditor = user?.roles?.includes('Editor');
  const canApprove = isAdmin || user?.roles?.includes('Qual Reviewer');
  const canDelete = isAdmin || isShootLead || isEditor;
  const canEditDirectorFields = isAdmin || isShootLead;
  // D-088: the Shoot stage plans camera segments in place (Producer / Power User / Admin); a card past Filming is locked.
  const canPlan = isAdmin || isShootLead;
  const planUnlocked = ['Scripted', 'Filming', 'Incomplete'].includes(status);
  const hasUnfilmed = segments.some(sg => sg.shootStatus !== 'filmed');
  // Shoot scheduler: the Production calendar's list view, on this production's row (Assigned with unfilmed segments, else Ready).
  const goToCalendarList = () => {
    const tab = hasUnfilmed ? 'assigned' : 'ready';
    navigate('/calendar', { state: {
      calendarInit:    { pageTab: 'assign', scriptListTab: tab },
      scriptListFocus: { tab, scriptId: record.scriptId || null, liveProductionId: record.id },
    } });
  };
  const canEditEditorFields = isAdmin || isEditor || isShootLead;
  // OUT-4 (docs/OUTPUT-C5-v1.md §4, rulings A, I, K): the producer credited Filmed, and a Riff card's minutes. Set by Admin,
  // Producer, Power User and Manager (the API's gate); read-only for everyone else.
  const canSetCardProducer = canSetProducer(user);
  const transitions = LIVE_TRANSITIONS[status] || [];
  const visibleTransitions = transitions.filter(t => t !== 'Approved' || canApprove);
  const isRiff = !record.scriptId;
  const isIncomplete = status === 'Incomplete';
  // Planned episode (065): the card exists but is not in production: a banner, no stage moves, the strip parked on Script.
  const isPlanned = record.productionActive === false;
  const canActivate = Boolean(user);   // §5b: the status route enforces only a signed-in user
  const [activating, setActivating] = useState(false);
  async function handleSendToProduction() {
    if (!canActivate || activating) return;
    setActivating(true);
    try { await api.post(`/api/live-production/${record.id}/activate`, {}); await onReload(); }
    catch (e) { toast(clean(e.response?.data?.error) || 'The episode was not sent to production. Try again.', { tone: 'danger' }); }
    finally { setActivating(false); }
  }
  const displayName = record.script?.scriptName || record.notes || '(Riff entry)';
  const editors = members.filter(m => m.roles?.some(r => r.role === 'Editor' || r.role === 'Power User'));
  // T-3: Producer-role members only (internal, active); a current producer outside that set still shows as the value.
  const shootLeads = members.filter(m => m.isActive !== false && !m.isExternal && m.roles?.some(r => r.role === 'Producer'));
  const currentProducerOutsideSet = form.shootLeadId && !shootLeads.some(m => m.id === form.shootLeadId)
    ? (members.find(m => m.id === form.shootLeadId) || record.shootLead || null) : null;
  const writerName = record.script?.writer?.name || members.find(m => m.id === record.script?.writerId)?.name || null;
  // The producer's options: Producer-role members (the Filmed board's people, ruling H), plus the current one if outside.
  const producerOutsideSet = form.producerId && !shootLeads.some(m => m.id === form.producerId)
    ? (members.find(m => m.id === form.producerId) || record.producer || null) : null;
  const producerOptions = [...(producerOutsideSet ? [{ value: producerOutsideSet.id, label: clean(producerOutsideSet.name) }] : []), ...shootLeads.map(m => ({ value: m.id, label: clean(m.name) }))];
  // "Mark filmed" waits for a producer as saved on the card; the API reads the card at the move.
  const holdOf = t => {
    const why = filmedHold(record, t, user);
    if (!why) return null;
    const unsaved = (form.producerId || '') !== (record.producerId || '');
    return unsaved ? `${why.replace(/^Set /, 'Save ')}` : why;
  };

  // ── handlers (modal parity) ─────────────────────────────────────────────────
  // Merge a PATCH / status response over the loaded record without letting a thin payload erase the relations.
  function applyUpdate(updated) {
    setRecord(prev => {
      const next = { ...prev, ...updated };
      for (const k of ['script', 'client', 'editor', 'producer', 'shootLead', 'talent', 'cookingPhase1', 'cookingPhase2', 'phase2PreassignedWriter', 'resolvedTalent']) {
        if (updated && updated[k] === undefined) next[k] = prev[k];
        else if (updated && updated[k] && typeof updated[k] === 'object' && prev[k] && typeof prev[k] === 'object' && !Array.isArray(updated[k])) next[k] = { ...prev[k], ...updated[k] };
      }
      return next;
    });
  }
  async function handleSave() {
    setSaving(true);
    try {
      const payload = { talentId: form.talentId || null, actorName: form.actorName || null, shootLeadId: form.shootLeadId || null, editorId: form.editorId || null, frameLink: form.frameLink || null, frameFolderLink: form.frameFolderLink || null, fileName: form.fileName || null, fileLocation: form.fileLocation || null, notes: form.notes || null, postedDate: form.postedDate || null, editScheduledDate: form.editScheduledDate || null, editNotes: form.editNotes || null };
      const card = cardChanges(record, form, parseMinutes);   // the producer and a Riff card's minutes, only when changed
      if (card.error) { toast(card.error.replace(' Leave it blank to use the estimate.', ''), { tone: 'warn' }); setSaving(false); return; }
      Object.assign(payload, card.payload);
      const res = await api.patch(`/api/live-production/${record.id}`, payload);
      applyUpdate(res.data); setDirty(false);
      setForm(f => ({ ...f, producerId: res.data.producerId ?? f.producerId, runtimeMinutes: res.data.runtimeMinutes !== undefined ? minutesText(res.data.runtimeMinutes) : f.runtimeMinutes }));
    } catch (err) { toast(clean(err.response?.data?.error) || 'The changes were not saved. Try again.', { tone: 'danger' }); } finally { setSaving(false); }
  }
  // Ruling K: a defaulted producer is confirmed by setting the same person explicitly, which clears the mark.
  const [confirmingProducer, setConfirmingProducer] = useState(false);
  async function handleConfirmProducer() {
    const body = confirmPayload(record);
    if (!body) return;
    setConfirmingProducer(true);
    try { const res = await api.patch(`/api/live-production/${record.id}`, body); applyUpdate({ producerDefaulted: false, ...res.data }); toast(`${clean(record.producer?.name) || 'The producer'} confirmed as producer.`); }
    catch (err) { toast(clean(err.response?.data?.error) || 'The producer was not confirmed. Try again.', { tone: 'danger' }); }
    finally { setConfirmingProducer(false); }
  }
  async function handleStatusChange(newStatus) {
    setTransitioning(true);
    try { const res = await api.patch(`/api/live-production/${record.id}/status`, { status: newStatus, changedBy: userId }); applyUpdate(res.data); const ns = STATUS_STAGE[res.data.status]; if (ns) setStage(ns); }
    catch (err) { toast(clean(err.response?.data?.error) || 'The stage did not change. Try again.', { tone: 'danger' }); } finally { setTransitioning(false); }
  }
  async function handleScheduleReshoot() {
    if (!reshootDate) { toast('Pick a reshoot date first.', { tone: 'warn' }); return; }
    setTransitioning(true);
    try { const res = await api.patch(`/api/live-production/${record.id}/schedule-reshoot`, { shootScheduledDate: reshootDate, changedBy: userId }); applyUpdate(res.data); }
    catch (err) { toast(clean(err.response?.data?.error) || 'The reshoot was not scheduled. Try again.', { tone: 'danger' }); } finally { setTransitioning(false); }
  }
  async function handleDelete() {
    const ok = await confirm({ title: 'Delete this production?', body: `${clean(displayName)} is deleted for good. The linked script is not affected.`, confirmLabel: 'Delete production', destructive: true });
    if (!ok) return;
    try { await api.delete(`/api/live-production/${record.id}`); navigate('/live'); }
    catch (err) { toast(clean(err.response?.data?.error) || 'The production was not deleted. Try again.', { tone: 'danger' }); }
  }
  async function handleDeleteVideo() {
    const ok = await confirm({ title: 'Archive this video?', body: `${videoAsset?.fileName} is archived, never deleted from storage. The file and its comments are kept and listed in the Review player's version menu, where they can be restored. Only an Admin can delete an archive for good.`, confirmLabel: 'Archive video', destructive: true });
    if (!ok) return;
    try { await api.delete(`/api/video-assets/${videoAsset.id}`); setVideoAsset(null); setTranscription(null); refreshArchived(); }
    catch (err) { toast(clean(err.response?.data?.error) || 'The video was not archived. Try again.', { tone: 'danger' }); }
  }
  async function handleReplace() {
    const ok = await confirm({ title: 'Upload a new version?', body: `When the new upload finishes, ${videoAsset?.fileName} is archived, never deleted from storage. Its comments are kept and listed in the Review player's version menu. If you cancel the upload, the current version stays.`, confirmLabel: 'Choose the new version' });
    if (ok) setReplacing(true);
  }
  function refreshArchived() { api.get(`/api/video-assets/archived?liveProductionId=${record.id}`).then(r => setArchivedVersions(r.data)).catch(() => {}); }
  async function handleExportCSV() { try { const res = await api.get(`/api/scripts/${record.scriptId}`); exportSceneCSV(res.data); } catch { toast('The CSV did not export. Try again.', { tone: 'danger' }); } }
  async function handleDownload() { try { const res = await api.get(`/api/video-assets/${videoAsset.id}/download`); openUrl(res.data.url); } catch { toast('The download did not start. Try again.', { tone: 'danger' }); } }
  async function handleZipDownload() {
    try {
      setZipJob({ status: 'processing' });
      const res = await api.post('/api/downloads/zip', { videoAssetIds: [videoAsset.id] }); const { jobId } = res.data;
      zipPollRef.current = setInterval(async () => { try { const poll = await api.get(`/api/downloads/zip/${jobId}`); if (poll.data.status === 'complete') { clearInterval(zipPollRef.current); setZipJob({ status: 'complete', downloadUrl: poll.data.downloadUrl }); } else if (poll.data.status === 'failed') { clearInterval(zipPollRef.current); setZipJob(null); toast('The zip was not built. Try again.', { tone: 'danger' }); } } catch { clearInterval(zipPollRef.current); setZipJob(null); } }, 3000);
    } catch { setZipJob(null); toast('The zip download did not start. Try again.', { tone: 'danger' }); }
  }
  async function handleTranscribe() {
    setTranscribing(true);
    try { await api.post('/api/transcriptions', { source_type: 'video_asset', source_id: videoAsset.id }); const r = await api.get(`/api/transcriptions/video_asset/${videoAsset.id}`); setTranscription(r.data); }
    catch { toast('The transcription did not start. Try again.', { tone: 'danger' }); } finally { setTranscribing(false); }
  }
  async function handleArchiveToggle() {
    setArchiving(true);
    try { const res = await api.patch(`/api/live-production/${record.id}/archive`, { archived: !record.isArchived }); applyUpdate({ isArchived: res.data.isArchived }); }
    catch (e) { toast(clean(e.response?.data?.error) || 'The production was not archived. Try again.', { tone: 'danger' }); } finally { setArchiving(false); }
  }
  async function handleProcessClips(footageId) {
    setAdcStartingIds(prev => new Set(prev).add(footageId));
    try { const titleOverride = record.script?.scriptName || record.notes || undefined; await api.post(`/api/adc/${footageId}/process`, titleOverride ? { titleOverride } : {}); loadRawFootage(); }
    catch (err) { toast(clean(err.response?.data?.error) || 'Process clips did not start. Try again.', { tone: 'danger' }); }
    finally { setAdcStartingIds(prev => { const n = new Set(prev); n.delete(footageId); return n; }); }
  }
  function toggleFootageSelectMode(on) { setFootageSelectMode(on); setFootageDeselected(new Set()); }
  function toggleClipSelected(cid) { setFootageDeselected(prev => { const n = new Set(prev); if (n.has(cid)) n.delete(cid); else n.add(cid); return n; }); }
  // F-1 (c): a cold-archived clip (archivedToArchiveId set) has no hot object and never enters the zip; when one sits on the
  // card, the request names the clips explicitly.
  const zipEligible = rawFootage.filter(c => !c.archivedToArchiveId);
  const selectedFootage = footageSelectMode ? zipEligible.filter(c => !footageDeselected.has(c.id)) : zipEligible;
  async function handleFootageZipDownload() {
    if (zipEligible.length === 0) { toast('Every clip on this production is in storage. Restore it there (Promote) to download.', { tone: 'warn' }); return; }
    if (footageSelectMode && selectedFootage.length === 0) { toast('Select at least one clip to download.', { tone: 'warn' }); return; }
    try {
      setFootageZip({ status: 'processing' });
      const body = { linkedVideoCardId: record.id, zipName: record.script?.scriptName || record.notes || 'raw-footage' }; if (footageSelectMode || zipEligible.length !== rawFootage.length) body.footageIds = selectedFootage.map(c => c.id);
      const res = await api.post('/api/downloads/footage-zip', body); const { jobId } = res.data;
      footageZipPollRef.current = setInterval(async () => { try { const poll = await api.get(`/api/downloads/zip/${jobId}`); if (poll.data.status === 'complete') { clearInterval(footageZipPollRef.current); openUrl(poll.data.downloadUrl); setFootageZip(null); } else if (poll.data.status === 'failed') { clearInterval(footageZipPollRef.current); setFootageZip(null); toast('The footage zip was not built. Try again.', { tone: 'danger' }); } } catch { clearInterval(footageZipPollRef.current); setFootageZip(null); } }, 3000);
    } catch { setFootageZip(null); toast('The download did not start. Try again.', { tone: 'danger' }); }
  }
  function goReview() { const returnTo = `/production/${record.id}?stage=review`; navigate(`/review/${videoAsset.id}`, { state: { returnTo } }); }
  const openArchived = () => navigate(`/review/${archivedVersions[0].id}`, { state: { returnTo: `/production/${record.id}?stage=review` } });
  const openScript = () => navigate('/scripts', { state: { openScriptId: record.script.id, fromVideoCard: true, isShootLeadForScript: isShootLead } });
  function copyLink() {
    navigator.clipboard.writeText(`${window.location.origin}/production/${record.id}`)
      .then(() => toast('Link to this production copied.'))
      .catch(() => toast('The link was not copied. Copy it from the address bar.', { tone: 'warn' }));
  }

  // ── derived ─────────────────────────────────────────────────────────────────
  const latestCutList = cutListInfo?.cutLists?.[0] || null;
  const railState = s => { if (isPlanned) return s === 'script' ? 'active' : 'waiting'; const si = STAGE_INDEX[s], ci = STAGE_INDEX[statusStage]; if (isIncomplete && s === 'shoot') return 'active'; if (si < ci) return 'done'; if (si === ci) return 'active'; return 'waiting'; };
  const cooking = !!record.isCooking;
  // Prisma models cooking_phase_link as one-to-many, so a Phase 1 record's cookingPhase2 arrives as an array: one row.
  const phase2 = Array.isArray(record.cookingPhase2) ? record.cookingPhase2[0] || null : record.cookingPhase2 || null;
  const recordForBanner = cooking ? { ...record, cookingPhase2: phase2 } : record;
  const phases = cooking ? [record.cookingPhase === 1 ? record : record.cookingPhase1, record.cookingPhase === 2 ? record : phase2] : [];
  const title = isEpisode ? `${record.episodeTitle || displayName}` : displayName;
  const clientName = clean(record.client?.name) || 'No client';
  const metaLine = isEpisode ? `${clientName}, episode ${record.episodeNumber}` : `${clientName}${cooking ? `, cooking phase ${record.cookingPhase}` : ''}${isRiff ? ', riff' : ''}`;
  const forward = isPlanned ? [] : visibleTransitions.filter(t => t !== 'Incomplete' && t !== BACK_STEP[status]);
  const moreItems = [
    !isRiff && { label: 'Export script scenes as CSV', icon: FileDown, onSelect: handleExportCSV },
    archivedVersions.length > 0 && { label: `Open archived video versions (${archivedVersions.length})`, icon: Archive, onSelect: openArchived },
    canDelete && { separator: true },
    canDelete && { label: 'Delete this production', icon: Trash2, danger: true, testId: 'production-delete', onSelect: handleDelete },
  ].filter(Boolean);
  const dockStage = stage === 'edit';
  const showDock = dockStage && dockOpen;

  usePageHeader({
    title: <span data-testid="production-title">{clean(title)}</span>,
    meta: metaLine,
    actions: (
      <>
        <Button variant="quiet" icon={ArrowLeft} onClick={() => navigate('/live')}>Live production</Button>
        {!inWorkbench && !narrow && <Button variant="primary" icon={MonitorPlay} data-testid="open-workbench" onClick={openWorkbench}>Open workbench</Button>}
        {/* the stage moves, rules unchanged; their order is the old footer's: forward, Mark incomplete, back, archive, More */}
        <div className="ui-cluster" data-testid="production-transitions">
          {isPlanned && <span className="ui-meta" data-testid="planned-no-advance">Stages advance after the episode is sent to production.</span>}
          {forward.map((t, i) => {
            const held = holdOf(t);
            // held (OUT-4): the button stays in place, disabled, with the sentence on its wrapper and under the header
            return held
              ? <span key={t} title={held}><Button variant="secondary" disabled aria-describedby="production-filmed-hold" data-testid="mark-filmed-held">{transitionLabel(t, false)}</Button></span>
              : <Button key={t} variant={inWorkbench && i === 0 ? 'primary' : 'secondary'} onClick={() => handleStatusChange(t)} disabled={transitioning}>{transitionLabel(t, false)}</Button>;
          })}
          {forward.some(holdOf) && <span className="ui-meta" id="production-filmed-hold" data-testid="filmed-hold">{forward.map(holdOf).find(Boolean)}.</span>}
          {!isPlanned && visibleTransitions.includes('Incomplete') && <Button variant="danger" icon={TriangleAlert} onClick={() => handleStatusChange('Incomplete')} disabled={transitioning}>Mark incomplete</Button>}
          {!isPlanned && BACK_STEP[status] && visibleTransitions.includes(BACK_STEP[status]) && <Button variant="quiet" icon={Undo2} onClick={() => handleStatusChange(BACK_STEP[status])} disabled={transitioning}>{transitionLabel(BACK_STEP[status], true)}</Button>}
          {status === 'Posted' && canEditDirectorFields && (
            <Button variant="quiet" icon={record.isArchived ? ArchiveRestore : Archive} loading={archiving} disabled={transitioning}
              title={record.isArchived ? 'Return this production to the Posted view' : 'File this Posted production away (recoverable through Show archived)'} onClick={handleArchiveToggle}>
              {record.isArchived ? 'Unarchive' : 'Archive'}
            </Button>
          )}
          {moreItems.length > 0 && (
            <Popover open={moreOpen} onOpenChange={setMoreOpen} align="end" label="More production actions"
              trigger={({ toggle, ref, props }) => <Button ref={ref} variant="quiet" iconOnly icon={MoreHorizontal} label="More" data-testid="production-overflow" onClick={toggle} {...props} />}>
              <Menu label="More production actions" items={moreItems} onClose={() => setMoreOpen(false)} />
            </Popover>
          )}
        </div>
      </>
    ),
  });

  // HOTFIX-1: below 1024 px the production is a read-only phone view (status, people, each video with Download), not the
  // Workbench; the header (title, client, the stage moves) is the same as on a computer.
  if (narrow) {
    return <PhoneView record={record} status={status} isIncomplete={isIncomplete} isPlanned={isPlanned} writerName={writerName}
      videoAsset={videoAsset} assetLoading={assetLoading} archivedVersions={archivedVersions} />;
  }

  // ── stage bodies ────────────────────────────────────────────────────────────
  const statusChips = (
    <div className="ui-cluster" data-testid="production-badges">
      <span data-testid="production-status" data-status={status}><StageChip stage={ladderOf(status)} /></span>
      {isIncomplete && <Badge condition="Incomplete" />}
      {status === 'Archived' && <Tag title="Set aside for now; it may shoot later">Set aside</Tag>}
      {isPlanned && <span data-testid="production-planned-pill"><Tag>Planned</Tag></span>}
      {record.isArchived && <Tag>Archived</Tag>}
      {latestCutList && (
        <button type="button" className="ui-plainlink" data-testid="badge-cutlist" title="A cut list is ready to download in the scrub dock on the Edit stage" onClick={() => setStage('edit')}>
          <Badge tone="ok">Cut list v{latestCutList.version}</Badge>
        </button>
      )}
      {(videoAsset?.openCommentCount ?? 0) > 0 && (
        <button type="button" className="ui-plainlink" onClick={() => setStage('review')}><Badge tone="warn">{plural(videoAsset.openCommentCount, 'open comment')}</Badge></button>
      )}
      {archivedVersions.length > 0 && <Tag>{plural(archivedVersions.length, 'archived version')}</Tag>}
    </div>
  );

  const details = (
    <section className="ui-section" aria-label="Production details" data-testid="production-people">
      <div className="ui-cluster">
        {statusChips}
        <span className="ui-spacer" />
        {videoAsset?.thumbnailUrl && (
          <button type="button" className="ui-plainlink" data-testid="production-thumb" title="Open the Review stage" onClick={() => setStage('review')}>
            <img className="ui-thumb" src={videoAsset.thumbnailUrl} alt="The current cut" />
          </button>
        )}
      </div>
      {isEpisode && (
        <p className="ui-p ui-dense ui-text-2" data-testid="series-label">
          Series <button type="button" className="ui-link" title="Open the series script (Longform)" onClick={() => navigate(`/scripts?script=${record.scriptId}`)}>{clean(record.script?.scriptName)}</button>,
          episode {record.episodeNumber}{sceneRange && (sceneRange.from != null || sceneRange.to != null) ? `, scenes ${sceneRange.from ?? 'start'} to ${sceneRange.to ?? 'end'}` : ''}, {clientName}
          <span className="ui-meta" data-testid="episode-subtitle"> ({clean(cardTitle(record))})</span>
        </p>
      )}
      <div className="ui-cols ui-cols--tight">
        <Select label="Producer" data-testid="producer-select" value={form.producerId} onChange={e => setField('producerId', e.target.value)} disabled={!canSetCardProducer}
          placeholder="Set the producer" options={producerOptions} />
        <Select label="Editor" value={form.editorId} onChange={e => setField('editorId', e.target.value)} disabled={!canEditEditorFields}
          placeholder="Not assigned" options={editors.map(m => ({ value: m.id, label: clean(m.name) }))} />
        {/* OUT-4: this field is the card's shoot lead (shootLeadId), labelled Producer before the card had a producer of its own */}
        <Select label="Shoot lead" data-testid="shootlead-select" value={form.shootLeadId} onChange={e => setField('shootLeadId', e.target.value)} disabled={!canEditDirectorFields}
          placeholder="Not assigned" options={[...(currentProducerOutsideSet ? [{ value: currentProducerOutsideSet.id, label: clean(currentProducerOutsideSet.name) }] : []), ...shootLeads.map(m => ({ value: m.id, label: clean(m.name) }))]} />
        {isRiff && (
          <Input label="Minutes" data-testid="card-runtime" inputMode="decimal" value={form.runtimeMinutes} onChange={e => setField('runtimeMinutes', e.target.value)}
            disabled={!canSetCardProducer} placeholder="Not set" />
        )}
      </div>
      {isRiff && record.runtimeMinutes == null && ['Filmed', 'Editing', 'Qual', 'Approved', 'Posted'].includes(status) && (
        <p className="ui-meta" data-testid="riff-minutes-pending">The Filmed credit is pending until the minutes are set.</p>
      )}
      {record.producerDefaulted && record.producer && (
        <ProducerDefaulted name={clean(record.producer.name)} canConfirm={canSetCardProducer} busy={confirmingProducer} onConfirm={handleConfirmProducer} />
      )}

      <dl className="ui-kv ui-kv--2">
        <dt>Talent{record.resolvedTalent?.kind === 'booked' ? ', booked' : record.resolvedTalent?.kind === 'mapped' ? ', from characters' : ''}</dt>
        {/* T-2: read-only. Booked, then character-mapped; talent is assigned when the shoot is booked on the Production calendar. */}
        <dd data-testid="talent-field" title={record.resolvedTalent?.kind ? 'Assigned on the Production calendar or the character registry' : 'Talent is assigned when the shoot is booked on the Production calendar'}>
          {record.resolvedTalent?.kind === 'booked' || record.resolvedTalent?.kind === 'mapped'
            ? clean(record.resolvedTalent.talents.map(t => t.name).join(', '))
            : <span className="ui-text-3">Assigned at booking</span>}
        </dd>
        <dt>Writer</dt>
        <dd>
          {record.script?.id
            ? <button type="button" className="ui-link" title="The writer is set on the script. Open it." onClick={() => navigate('/scripts', { state: { openScriptId: record.script.id, fromVideoCard: true } })}>{clean(writerName) || 'No writer'}</button>
            : <span className="ui-text-3" title="Riff productions have no script">{isRiff ? 'Riff, no script' : 'No writer'}</span>}
        </dd>
        <dt>Shoot scheduled</dt><dd>{dayCell(record.shootScheduledDate)}</dd>
        <dt>Filmed</dt><dd>{dayCell(record.dateFilmed)}</dd>
        <dt>Edit scheduled</dt><dd>{dayCell(record.editScheduledDate)}</dd>
        <dt>Posted</dt><dd>{dayCell(record.postedDate)}</dd>
      </dl>
      <div className="ui-cluster">
        {record.script?.id && <Button variant="quiet" icon={ExternalLink} onClick={openScript}>View script</Button>}
        <Button variant="quiet" icon={Copy} onClick={copyLink}>Copy link</Button>
        <Button variant="quiet" icon={CalendarDays} data-testid="shoot-scheduler" title="Open this production's row in the Production calendar's Script list" onClick={goToCalendarList}>Shoot scheduler</Button>
      </div>
      {isIncomplete && (
        <div className="ui-region ui-region--pad ui-toolbar ui-toolbar--end" aria-label="Reshoot">
          <DatePicker label="Reshoot date" value={reshootDate} onChange={e => setReshootDate(e.target.value)} />
          <Button variant="secondary" onClick={handleScheduleReshoot} disabled={!canEditDirectorFields || transitioning || !reshootDate}>Schedule and resume filming</Button>
        </div>
      )}
    </section>
  );

  const phaseRows = (withPosted) => phases.map((ph, i) => (ph ? (
    <div key={i} className="ui-cluster">
      <span className="ui-dense">Phase {i + 1}{ph.id === record.id ? ' (this production)' : ''}</span>
      <StageChip stage={ladderOf(ph.status)} />
      {withPosted ? <span className="ui-dense ui-text-2">Posted {dayCell(ph.postedDate)}</span> : <span className="ui-dense ui-text-2">{clean(ph.script?.scriptName) || 'No script yet'}</span>}
      {ph.id !== record.id && <Button variant="quiet" icon={ExternalLink} onClick={() => navigate(`/production/${ph.id}`)}>Open</Button>}
    </div>
  ) : withPosted ? null : (
    <div key={i} className="ui-cluster"><span className="ui-dense">Phase {i + 1}</span><span className="ui-meta">Not created yet. Use Write Phase 2 script above.</span></div>
  )));

  const scriptStage = (<>
    {isRiff ? (
      <Input label="Video title (riff, no script)" data-testid="riff-title" value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Riff title"
        readOnly={!canEditEditorFields} hint="Riff productions are shot freestyle; the title is stored as the card's notes." />
    ) : (<>
      <dl className="ui-kv ui-kv--2">
        <dt>Script</dt><dd>{clean(record.script?.scriptName)}{record.script?.version ? <span className="ui-tc ui-text-2"> v{record.script.version}</span> : null}</dd>
        <dt>Format</dt><dd>{clean(record.script?.scriptBody?.format) || 'Not set'}</dd>
        <dt>Qual status</dt><dd>{clean(record.script?.qualStatus) || 'Not set'}</dd>
      </dl>
      {Array.isArray(record.script?.scriptBody?.scenes) && (
        <section className="ui-section" aria-label="Script preview" data-testid="script-preview">
          <h2 className="ui-section__title">Preview</h2>
          <Table caption="The script's first 40 lines" maxHeight={320} rowKey={r => r.key}
            columns={[
              { key: 'n', label: 'Row', width: 64, render: r => <span className="ui-tc">{r.n}</span> },
              { key: 'character', label: 'Character', width: 160, render: r => clean(r.character) },
              { key: 'line', label: 'Line', render: r => <span title={r.line}>{r.line}</span> },
            ]}
            rows={record.script.scriptBody.scenes.filter(sc => sc && (sc.line || sc.character)).slice(0, 40).map((sc, i) => ({ key: sc.id || `row-${i}`, n: i + 1, character: sc.character || '', line: sc.line || '' }))} />
        </section>
      )}
      <div className="ui-cluster">
        <Button variant="secondary" icon={ExternalLink} onClick={openScript}>View script</Button>
        <Button variant="quiet" icon={FileDown} data-testid="export-csv" onClick={handleExportCSV}>Export CSV</Button>
      </div>
      {record.script?.id && <ScriptCast scriptId={record.script.id} clientId={record.clientId} user={user} />}
    </>)}
    {cooking && phases.some(Boolean) && (
      <section className="ui-section" data-testid="cooking-phases" aria-label="Cooking phases">
        <h2 className="ui-section__title">Cooking, both phases</h2>
        {phaseRows(false)}
      </section>
    )}
    {!isRiff && (
      <label className="ui ui-field">
        <span className="ui-field__label" data-testid="grid-note-label">Grid note (shows in the Live grid's Notes column)</span>
        <textarea className="ui-input ui-textarea" rows={3} data-testid="notes" value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="A short note for the Live grid" readOnly={!canEditEditorFields} />
      </label>
    )}
  </>);

  const segmentColumns = [
    { key: 'label', label: 'Segment', width: 160, render: sg => clean(sg.segmentLabel) },
    { key: 'day', label: 'Shoot day', width: 200, render: sg => (sg.shootDay
      ? <span className="ui-tc">{formatDate(sg.shootDay)}{sg.shootTimeSlot ? `, ${sg.shootTimeSlot}` : ''}</span>
      : <span className="ui-text-3">No day{sg.shootTimeSlot ? `, ${sg.shootTimeSlot}` : ''}</span>) },
    { key: 'status', label: 'Status', width: 120, render: sg => (sg.shootStatus === 'filmed' ? <Badge tone="ok">Filmed</Badge> : <Tag>{sentence(sg.shootStatus) || 'Not filmed'}</Tag>) },
    { key: 'talent', label: 'Talent', width: 280, render: sg => clean((sg.talents || []).map(t => t.talent?.name || t.talentDisplayName).filter(Boolean).join(', ')) || <span className="ui-text-3">No talent</span> },
    { key: 'setting', label: 'Setting', width: 200, render: sg => clean(sg.setting) || '' },
  ];
  const shootStage = (<>
    {/* D-088: the shoot day is derived from the camera segments; segments are planned here (the Schedule plan dialog in
        card mode) or from the Production calendar's list view. */}
    <section className="ui-section" data-testid="shoot-plan" aria-label="Camera segments and bookings">
      <div className="ui-section__head ui-section__head--center">
        <h2 className="ui-section__title">Camera segments and bookings</h2>
        {segments.length > 0 && <span className="ui-meta">{segments.filter(sg => sg.shootStatus === 'filmed').length} of {segments.length} filmed</span>}
        <span className="ui-section__actions">
          {canPlan && (
            <Button variant="secondary" icon={hasUnfilmed ? Pencil : CalendarDays} data-testid="plan-shoot" disabled={!planUnlocked}
              title={planUnlocked ? (hasUnfilmed ? 'Edit the camera segments, talent and shoot days' : 'Plan the camera segments and assign talent') : `The shoot plan is locked once a production is ${status}. Mark it incomplete for a reshoot.`}
              onClick={() => setPlanOpen(true)}>{hasUnfilmed ? 'Edit shoot plan' : 'Plan shoot segments'}</Button>
          )}
          <Button variant="quiet" icon={ExternalLink} data-testid="open-calendar-list" title="Open this production in the Production calendar's Script list" onClick={goToCalendarList}>Production calendar</Button>
        </span>
      </div>
      {segments.length === 0
        ? <p className="ui-p ui-dense ui-text-2" data-testid="segments-empty">No camera segments yet. Plan them here{canPlan ? '' : ' (Producer, Power User or Admin)'} or from the Production calendar's Script list.</p>
        : (
          <div data-testid="segments">
            <Grid columns={segmentColumns} rows={segments} rowKey={sg => sg.id} density="medium" storageKey="hf-grid:production-segments" label="Camera segments" height={`${Math.min(360, 32 + segments.length * 40 + 2)}px`} />
          </div>
        )}
    </section>
    {planOpen && (
      <SchedulePlanModal
        card={{ id: record.id, title: displayName, clientName: record.client?.name || '', scriptId: record.scriptId || null, segments }}
        onClose={() => setPlanOpen(false)}
        onConfirmed={() => { setPlanOpen(false); onReload(); }}
      />
    )}
    <FootageList rawFootage={rawFootage} playingClipId={playingClipId} setPlayingClipId={setPlayingClipId} footageSelectMode={footageSelectMode} toggleFootageSelectMode={toggleFootageSelectMode}
      footageDeselected={footageDeselected} toggleClipSelected={toggleClipSelected} selectedFootage={selectedFootage} footageZip={footageZip} handleFootageZipDownload={handleFootageZipDownload}
      adcStartingIds={adcStartingIds} handleProcessClips={handleProcessClips} transcribingIds={transcribingIds} handleTranscribeClip={handleTranscribeClip} canTranscribe
      faceCast={faceCast} faceChars={faceChars} onClipFaces={onClipFaces} />
  </>);

  const sceneClips = rawFootage.filter(c => c.parentFootageId).length;
  const editStage = (<>
    <div className="ui-cols ui-cols--tight">
      <Select label="Editor" value={form.editorId} onChange={e => setField('editorId', e.target.value)} disabled={!canEditEditorFields}
        placeholder="Not assigned" options={editors.map(m => ({ value: m.id, label: clean(m.name) }))} />
      <DatePicker label="Edit scheduled" data-testid="edit-date" value={form.editScheduledDate} onChange={e => setField('editScheduledDate', e.target.value)} disabled={!canEditEditorFields} />
    </div>
    {/* D-131: the old free-text Edit notes box is the Notes panel (its text was carried over as a note). */}
    {sceneClips > 0 && (
      <p className="ui-p ui-dense ui-text-2" data-testid="adc-context">
        {plural(sceneClips, 'scene clip')} extracted by Process clips. Play or download them on the <button type="button" className="ui-link" onClick={() => setStage('shoot')}>Shoot stage</button>.
      </p>
    )}
    {!dockOpen && (
      <p className="ui-p ui-dense ui-text-3">The scrub dock is closed. <button type="button" className="ui-link" onClick={() => setDock(true)}>Open the scrub dock</button></p>
    )}
  </>);

  const ts = videoAsset?.transcodingStatus; const isPreparing = ts === 'pending' || ts === 'processing';
  const videoBody = assetLoading ? <Skeleton variant="block" height={240} label="Loading the video" />
    : videoAsset && replacing ? (
      <div className="ui-stack" data-testid="replace-uploader">
        {upload
          ? <p className="ui-p ui-dense ui-text-2" role="status">{upload.status === 'failed' ? `${clean(upload.name)} did not upload. Retry it from the upload sheet.` : `Uploading ${clean(upload.name)}, ${Math.round(upload.progress * 100)}%. You can keep working; the upload sheet shows its progress.`}</p>
          : <VideoDrop onFile={takeVideo} message={`Drop the new version of ${videoAsset.fileName} here, or choose the file. The current version is archived when this upload finishes.`} />}
        <span><Button variant="quiet" icon={Undo2} data-testid="replace-cancel" onClick={() => setReplacing(false)}>Cancel and keep the current version</Button></span>
      </div>
    ) : videoAsset ? (
      // F-2: video.js runs in fill mode, so the player sits in a 16:9 frame with a definite height; the file line is outside it.
      <div className="ui ui-monitor ui-monitor--capped" data-testid="review-player-frame">
        <div className="ui-monitor__frame ui-monitor__frame--16x9" data-testid="review-player">
          {isPreparing ? (
            <>
              {videoAsset.thumbnailUrl && <img className="ui-contain" src={videoAsset.thumbnailUrl} alt="" />}
              <div className="ui-frame-note" role="status">
                <p className="ui-state__title">Preparing the video for playback</p>
                <p className="ui-state__msg">Refresh in a minute or two.</p>
              </div>
            </>
          ) : <VideoPlayer key={videoAsset.id} src={videoAsset.playbackUrl || videoAsset.cloudfrontUrl} poster={videoAsset.thumbnailUrl || undefined} />}
        </div>
        <span className="ui-cluster ui-cluster--nowrap">
          <span className="ui-dense ui-truncate ui-grow">{videoAsset.fileName}</span>
          {(videoAsset.openCommentCount ?? 0) > 0 && <span className="ui-meta">{plural(videoAsset.openCommentCount, 'open comment')}</span>}
          <Button variant="secondary" icon={Play} data-testid="open-in-review" title="Comments on the timeline, versions, approve" onClick={goReview}>Open in review</Button>
        </span>
      </div>
    ) : upload ? (
      <p className="ui-p ui-dense ui-text-2" role="status">{upload.status === 'failed' ? `${clean(upload.name)} did not upload. Retry it from the upload sheet.` : `Uploading ${clean(upload.name)}, ${Math.round(upload.progress * 100)}%. You can keep working; the upload sheet shows its progress.`}</p>
    ) : canEditEditorFields ? <VideoDrop onFile={takeVideo} message={`Drop the cut of ${clean(displayName)} here, or choose the file. MP4, MOV, WebM or AVI.`} />
    : <p className="ui-p ui-dense ui-text-3">No video uploaded yet.</p>;
  const reviewStage = (<>
    <section className="ui-section" aria-label="Video">
      <h2 className="ui-section__title">Video</h2>
      {videoBody}
    </section>
    {videoAsset && !replacing && (
      <div className="ui-cluster" data-testid="video-actions">
        {canEditEditorFields && <Button variant="secondary" icon={Upload} data-testid="upload-new-version" onClick={handleReplace}>Upload new version</Button>}
        <ShareLinkManager videoAssetId={videoAsset.id} />
        {(!transcription || transcription.status === 'failed') && <Button variant="quiet" loading={transcribing} onClick={handleTranscribe}>{transcription?.status === 'failed' ? 'Retry transcribe' : 'Transcribe'}</Button>}
        {(transcription?.status === 'pending' || transcription?.status === 'processing') && <Badge tone="info">Transcribing</Badge>}
        {transcription?.status === 'complete' && <Badge tone="ok">Transcript ready</Badge>}
        <Button variant="quiet" icon={Download} onClick={handleDownload}>Download video</Button>
        {zipJob?.status !== 'complete' && <Button variant="quiet" icon={Download} data-testid="video-zip" loading={zipJob?.status === 'processing'} onClick={handleZipDownload}>{zipJob?.status === 'processing' ? 'Preparing zip' : 'Download as zip'}</Button>}
        {zipJob?.status === 'complete' && zipJob.downloadUrl && <Button variant="secondary" icon={Download} data-testid="video-zip-ready" onClick={() => window.open(zipJob.downloadUrl, '_blank', 'noopener')}>Download zip</Button>}
        {archivedVersions.length > 0 && <Button variant="quiet" icon={Archive} onClick={openArchived}>{plural(archivedVersions.length, 'archived version')}</Button>}
        {(canDelete || videoAsset.uploadedBy === user?.id) && <Button variant="danger" icon={Archive} data-testid="video-archive" onClick={handleDeleteVideo}>Archive video</Button>}
      </div>
    )}
    <div className="ui-cols ui-cols--tight">
      <Input label="File name" data-testid="file-name" value={form.fileName} onChange={e => setField('fileName', e.target.value)} placeholder="For example: Rim Job P1" readOnly={!canEditEditorFields} />
      <Input label="File location" value={form.fileLocation} onChange={e => setField('fileLocation', e.target.value)} placeholder="SSD, Frame.io, cloud" readOnly={!canEditEditorFields} />
    </div>
  </>);

  const postStage = <PostStage form={form} setField={setField} canEdit={canEditEditorFields} record={record} clientName={clientName} scheduler={scheduler}
    navigate={navigate} cooking={cooking} phases={phases} phaseRows={phaseRows} />;

  const body = { script: scriptStage, shoot: shootStage, edit: editStage, review: reviewStage, post: postStage }[stage];

  return (
    <div className="ui ui-page ui-page--flush" data-testid="production-page" data-status={status} data-stage={stage} data-planned={isPlanned ? 'true' : 'false'} data-dock={showDock ? 'open' : 'closed'}>
      {/* ── stage strip: the page's stages as a numbered sequence (§3.2) ── */}
      <nav className="ui-inset ui-steps" aria-label="Stages">
        <div className="ui-steps__list" role="tablist" aria-label="Stages" data-testid="production-rail">
          {STAGES.map((s, i) => { const st = railState(s); return (
            <button key={s} type="button" role="tab" aria-selected={stage === s} data-testid={`stage-${s}`} data-state={st} className="ui-steps__item" onClick={() => setStage(s)}>
              <span className="ui-steps__num" aria-hidden="true">{i + 1}</span>
              <span>{STAGE_LABEL[s]}</span>
              <span className="ui-meta">{RAIL_WORD[st]}</span>
            </button>
          ); })}
        </div>
      </nav>
      <div className="ui-wb">
        <div className="ui-wb__main" data-testid={`stage-body-${stage}`}>
          {isPlanned && (
            <div className="ui-region ui-region--pad ui-cluster" data-testid="planned-banner">
              <Tag>Planned</Tag>
              <span className="ui-dense ui-text-2 ui-grow">Planned, not yet in production. This episode is listed only on its series page and here. It joins the Live grid, the Script list and the schedules once it is sent to production.</span>
              {canActivate && <Button variant="secondary" icon={Send} data-testid="send-to-production" loading={activating} onClick={handleSendToProduction}>Send to production</Button>}
            </div>
          )}
          {details}
          {cooking && <CookingPhaseBanner record={recordForBanner} members={members} user={user} onRecordUpdated={applyUpdate} />}
          {body}
        </div>
        {showDock && (
          <section className="ui-wb__dock" data-testid="scrub-dock" aria-label="Scrub dock">
            {record.scriptId && scriptHasDialogue
              ? <SceneScrubPanel sceneRange={sceneRange} key={`cut-${record.id}-${completedTranscripts}`} cardId={record.id} canBuild onCollapse={() => setDock(false)} onBuilt={loadCutListInfo} />
              : (
                <div className="ui-chat">
                  <div className="ui-drawer__head">
                    <h2 className="ui-heading">Scrub</h2>
                    <Button variant="quiet" iconOnly icon={PanelRightOpen} label="Collapse the scrub dock" onClick={() => setDock(false)} />
                  </div>
                  <div className="ui-thread ui-thread--drawer">
                    <p className="ui-p ui-dense ui-text-2" data-testid="scene-scrub-unavailable-edit">
                      {isRiff ? 'A riff has no script, so there is nothing to scrub.' : 'The script has no dialogue yet. Paste the episode dialogue into the script to scrub this production.'}
                    </p>
                  </div>
                </div>
              )}
          </section>
        )}
        {panel === 'notes' && (
          <aside className="ui-wb__panel" aria-label="Notes">
            <ProductionNotes cardId={record.id} user={user} />
          </aside>
        )}
        {panel === 'file' && (
          <aside className="ui-wb__panel" aria-label="Project file">
            <div className="ui-drawer__head"><h2 className="ui-heading">Project file</h2></div>
            <div className="ui-thread ui-thread--drawer">
              <ProjectFileField record={record} canEdit={canEditEditorFields} members={members} onChange={pf => applyUpdate(pf)} />
            </div>
          </aside>
        )}
        <div className="ui-wb__tabs" role="group" aria-label="Side panels">
          {dockStage && (
            <Button variant="quiet" iconOnly icon={PanelRightOpen} label={dockOpen ? 'Collapse the scrub dock' : 'Open the scrub dock'} aria-pressed={dockOpen} onClick={() => setDock(!dockOpen)} />
          )}
          <Button variant="quiet" iconOnly icon={NotebookPen} label="Notes" aria-pressed={panel === 'notes'} onClick={() => togglePanel('notes')} />
          <Button variant="quiet" iconOnly icon={FileBox} label="Project file" aria-pressed={panel === 'file'} onClick={() => togglePanel('file')} />
        </div>
      </div>
      {dirty && (
        <div className="ui-footbar ui-inset ui-inset--bar ui-toolbar" data-testid="production-savebar" role="toolbar" aria-label="Unsaved changes">
          <span className="ui-dense ui-text-2 ui-grow">Unsaved changes</span>
          <Button variant="quiet" onClick={() => { setForm(blankForm(record)); setDirty(false); }}>Discard</Button>
          <Button variant="primary" data-testid="production-save" loading={saving} onClick={handleSave}>Save changes</Button>
        </div>
      )}
    </div>
  );
}

// Post stage: posted date, channel, the posting queue rows, cooking posting by phase, the Frame.io-era links.
function PostStage({ form, setField, canEdit, record, clientName, scheduler, navigate, cooking, phases, phaseRows }) {
  const [legacyOpen, setLegacyOpen] = useState(false);
  const columns = [
    { key: 'track', label: 'Track', width: 160, render: sl => <Tag>{clean(sl.track)}</Tag> },
    { key: 'slot', label: 'Slot', width: 120, render: sl => <span className="ui-tc">{sl.slotOrder}</span> },
    { key: 'date', label: 'Scheduled', width: 200, render: sl => {sl.scheduledDate ? <span className="ui-tc">{formatDate(sl.scheduledDate)}</span> : <span className="ui-text-3">Unscheduled</span>} },
  ];
  return (<>
    <div className="ui-cols ui-cols--tight">
      <DatePicker label="Posted date" data-testid="posted-date" value={form.postedDate} onChange={e => setField('postedDate', e.target.value)} disabled={!canEdit} />
      <dl className="ui-kv"><dt>Channel (client)</dt><dd>{clientName}{record.client?.serviceType ? `, ${clean(record.client.serviceType)}` : ''}</dd></dl>
    </div>
    <section className="ui-section" aria-label="Posting scheduler">
      <div className="ui-section__head ui-section__head--center">
        <h2 className="ui-section__title">Posting scheduler</h2>
        <span className="ui-section__actions"><Button variant="quiet" icon={ExternalLink} onClick={() => navigate('/scheduler')}>Open posting scheduler</Button></span>
      </div>
      {scheduler.length === 0
        ? <p className="ui-p ui-dense ui-text-2">Not in the posting queue.</p>
        : (
          <div data-testid="scheduler-rows">
            <Grid columns={columns} rows={scheduler} rowKey={sl => sl.id} density="medium" storageKey="hf-grid:production-scheduler" label="Posting queue slots"
              onOpen={() => navigate('/scheduler')} height={`${Math.min(320, 32 + scheduler.length * 40 + 2)}px`} />
          </div>
        )}
    </section>
    {cooking && phases.some(Boolean) && (
      <section className="ui-section" aria-label="Cooking posting by phase">
        <h2 className="ui-section__title">Cooking, posting by phase</h2>
        {phaseRows(true)}
      </section>
    )}
    <section className="ui-stack ui-stack--tight" data-testid="legacy-links">
      <span>
        <button type="button" className="ui-disclosure ui-dense ui-text-2" aria-expanded={legacyOpen} onClick={() => setLegacyOpen(v => !v)}>
          <Icon icon={ChevronDown} />Legacy links (Frame.io era)
        </button>
      </span>
      {legacyOpen && (
        <div className="ui-cols ui-cols--tight">
          <Input label="Frame link" data-testid="frame-link" value={form.frameLink} onChange={e => setField('frameLink', e.target.value)} placeholder="https://" readOnly={!canEdit} />
          <Input label="Frame folder link" value={form.frameFolderLink} onChange={e => setField('frameFolderLink', e.target.value)} placeholder="https://" readOnly={!canEdit} />
        </div>
      )}
    </section>
  </>);
}

// Drop a video here or choose one; it goes to the global Upload sheet and uploads in the background (replaces VideoUploader).
function VideoDrop({ onFile, message }) {
  const input = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div className="ui-dropzone" data-drop={over || undefined}
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); onFile(e.dataTransfer.files?.[0]); }}>
      <span className="ui-dense ui-text-2">{message}</span>
      <Button variant="secondary" icon={Upload} onClick={() => input.current?.click()}>Choose video</Button>
      <input ref={input} type="file" accept={ACCEPTED_EXT} hidden onChange={e => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
}

// OUT-4 (C5 §4, ruling K): a producer the API set at Filmed, under Details until someone in the gate confirms or changes it.
function ProducerDefaulted({ name, canConfirm, busy, onConfirm }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ui-stack ui-stack--tight" data-testid="producer-defaulted">
      <button type="button" className="ui-disclosure ui-output__disclosure" aria-expanded={open} aria-controls="producer-defaulted-body" onClick={() => setOpen(o => !o)}>
        <Icon icon={ChevronDown} />Details
      </button>
      {open && (
        <div className="ui-stack ui-stack--tight" id="producer-defaulted-body">
          <div className="ui-cluster">
            <span className="ui-dense">Producer: {name} (defaulted)</span>
            {canConfirm && <Button variant="secondary" loading={busy} data-testid="producer-confirm" onClick={onConfirm}>Confirm</Button>}
          </div>
          <p className="ui-meta">Set when the card was marked filmed with no producer: the shoot lead, or else the person who made the move.</p>
        </div>
      )}
    </div>
  );
}
